import React from 'react';
import { useSelector } from 'react-redux';
import { useDrawerNavigation } from '../context/DrawerNavigationContext';
import { useCurrentPath } from '../context/CurrentPathContext';
import NavItem from './NavItem';
import {
	getNavItemKey,
	resolveDirectNavActiveSectionId,
	isFragmentItem
} from '../utils/navItemUtils';

/**
 * NavItemList — renders the top-level navigation item list.
 * Reads items from Redux navData slice and navigation state from context.
 * No props required — all state is sourced from context/Redux.
 *
 * Computes resolvedActiveSectionId once at this level so the "exactly one
 * path-match" fallback (direct navigation without _ddc_nav_drawer) can be
 * applied consistently across all items rather than independently per-item.
 */
const NavItemList = () => {
	const items = useSelector((state) => state.navData?.items ?? []);
	const { onSubmenuOpen } = useDrawerNavigation();
	const currentPath = useCurrentPath();

	// Fragments (embedded slots / fragment-wrappers) are not top-level nav
	// entries — they belong in a second-level drilldown. The nav service
	// sometimes places one (e.g. rec-vehicles) at the top of the tree; keep those
	// out of level 1 so they don't render as siblings of real sections.
	const topLevelItems = items.filter((item) => !isFragmentItem(item));

	// When the page URL has no _ddc_nav_drawer param, fall back to path-only
	// matching. If exactly one top-level section has a descendant whose path
	// matches, that section wins. If zero or multiple match, nothing wins.
	const resolvedActiveSectionId = resolveDirectNavActiveSectionId(
		items,
		currentPath
	);

	if (topLevelItems.length === 0) {
		return null;
	}

	return (
		<ul
			className="ws-nav-drawer__nav-list list-unstyled m-0 overflow-auto flex-column align-items-start align-self-stretch"
			data-testid="nav-item-list"
		>
			{topLevelItems.map((item, index) => (
				<NavItem
					key={getNavItemKey(item, index)}
					item={item}
					onSubmenuOpen={onSubmenuOpen}
					resolvedActiveSectionId={resolvedActiveSectionId}
				/>
			))}
		</ul>
	);
};

export default NavItemList;
